import './style.css';

import {
  BuildingViewerBuildingData,
  BuildingViewerData,
  BuildingViewerFloorData,
  BuildingViewerRoomData,
} from '../../../../Content';
import { useCallback, useEffect, useState } from 'react';

import { BuildingPicker } from './BuildingPicker';
import { FloorPicker } from './FloorPicker';
import { PickerHeader } from './PickerHeader';
import { UnitPicker } from './UnitPicker';
import { useScene } from '../../../../contexts/SceneContext';

export interface Props {
  content: BuildingViewerData;
}

export const BuildingViewerContent: React.FC<Props> = ({ content }) => {
  const { parseContentPath } = useScene();

  const [activeBuilding, setActiveBuilding] =
    useState<BuildingViewerBuildingData | null>(null);
  const [activeFloor, setActiveFloor] =
    useState<BuildingViewerFloorData | null>(null);
  const [activeRoom, setActiveRoom] =
    useState<BuildingViewerRoomData | null>(null);

  const selectBuilding = useCallback(
    (building: BuildingViewerBuildingData | null) => {
      if (activeBuilding === building) return;
      setActiveBuilding(building);
      setActiveFloor(building?.floors[0] || null);
      setActiveRoom(building?.floors[0]?.rooms[0] || null);
    },
    [activeBuilding]
  );

  const selectFloor = useCallback(
    (floor: BuildingViewerFloorData | null) => {
      if (activeFloor === floor) return;
      setActiveFloor(floor);
      setActiveRoom(floor?.rooms[0] || null);
    },
    [activeFloor]
  );

  useEffect(() => {
    selectBuilding(content.buildings[0]);
  }, [content, selectBuilding]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: '1fr 1fr 1fr',
        gridTemplateColumns: '1.8em 33.33% auto',
        userSelect: 'none',
      }}
    >
      {/* HEADERS */}
      <div style={{ gridArea: '1 / 1 / span 1 / span 1', overflow: 'hidden' }}>
        <PickerHeader text="select building" />
      </div>
      <div style={{ gridArea: '2 / 1 / span 1 / span 1', overflow: 'hidden' }}>
        <PickerHeader text="select floor" />
      </div>
      <div style={{ gridArea: '3 / 1 / span 1 / span 1', overflow: 'hidden' }}>
        <PickerHeader text="select unit" />
      </div>

      {/* PICKERS */}
      <div style={{ gridArea: '1 / 2 / span 1 / span 1' }}>
        <BuildingPicker
          src={parseContentPath(content.src)}
          buildings={content.buildings}
          activeBuilding={activeBuilding}
          onBuildingClick={(building) => selectBuilding(building)}
        />
      </div>
      <div style={{ gridArea: '2 / 2 / span 1 / span 1' }}>
        {activeBuilding && (
          <FloorPicker
            src={parseContentPath(activeBuilding.src)}
            floors={activeBuilding.floors}
            activeFloor={activeFloor}
            onFloorClick={(floor) => selectFloor(floor)}
          />
        )}
      </div>
      <div style={{ gridArea: '3 / 2 / span 1 / span 1' }}>
        {activeFloor && (
          <UnitPicker
            src={parseContentPath(activeFloor.src)}
            rooms={activeFloor.rooms}
            activeRoom={activeRoom}
            onRoomClick={(room) => setActiveRoom(room)}
          />
        )}
      </div>

      {/* Unit Floorplan */}
      {activeRoom && (
        <div
          style={{
            gridArea: '1 / 3 / span 3 / span 1',
            backgroundImage: `url("${parseContentPath(activeRoom.src)}")`,
            backgroundSize: 'cover',
          }}
        />
      )}
    </div>
  );
};
